import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Toast = { id: number; title: string; body?: string; tone: 'info' | 'success' | 'error' };
const Ctx = createContext<{ push: (t: Omit<Toast, 'id'>) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 5500);
  }, []);
  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2 w-80">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'card card-pad text-sm border-l-4',
              t.tone === 'success' && 'border-l-emerald-500',
              t.tone === 'error' && 'border-l-rdx',
              t.tone === 'info' && 'border-l-blue-500'
            )}
          >
            <div className="font-semibold">{t.title}</div>
            {t.body && <div className="text-muted text-xs mt-1 whitespace-pre-line">{t.body}</div>}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be inside <ToastProvider>');
  return v;
}
